// ============================================
// FAQ 用户管理页面（/faq/uadmin/users）
// 仅 super_admin 可见，管理 is_faq_admin 权限
// ============================================

import { useState, useEffect } from 'react';
import {
  Search, Users, Shield, ShieldCheck, Loader2, RefreshCw,
  Mail, Calendar, UserPlus, X, Send, Check, AlertCircle,
} from 'lucide-react';
import { supabase } from '../config/supabase';
import { deriveExternalUserFlag } from '../utils/user-scope';
import { getFaqAuthRedirectUrl } from './faq-auth-redirect';
import { useFaqSpace } from './FaqSpaceContext';

interface FaqUser {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  role: string;
  is_faq_admin: boolean;
  is_external: boolean;
  created_at: string;
}

function isExternalUser(user: FaqUser) {
  return deriveExternalUserFlag(user.email, user.is_external);
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : '操作失败';
}

export default function FaqUsersPage() {
  const { basePath } = useFaqSpace();
  const [users, setUsers] = useState<FaqUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'admin' | 'normal'>('all');
  const [audienceMode, setAudienceMode] = useState<'all' | 'internal' | 'external'>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 邀请相关
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  async function fetchUsers() {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('v_fs_users')
        .select('id, email, name, avatar_url, role, is_faq_admin, is_external, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data ?? []);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  async function toggleFaqAdmin(user: FaqUser) {
    setTogglingId(user.id);
    try {
      const { error } = await supabase
        .from('fs_user_profiles')
        .update({ is_faq_admin: !user.is_faq_admin })
        .eq('id', user.id);

      if (error) throw error;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, is_faq_admin: !u.is_faq_admin } : u
        )
      );
    } catch (e: unknown) {
      console.error('Toggle FAQ admin failed:', e);
      setError(`操作失败: ${getErrorMessage(e)}`);
    } finally {
      setTogglingId(null);
    }
  }

  // 邀请用户
  async function inviteUser() {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('未登录');

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-faq-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            email: inviteEmail.trim(),
            redirectTo: getFaqAuthRedirectUrl(basePath),
          }),
        }
      );

      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || '邀请失败');

      if (result.existed) {
        setInviteResult({ type: 'success', message: `${inviteEmail} 已有账号，已授予 FAQ 管理员权限` });
      } else {
        setInviteResult({ type: 'success', message: `邀请邮件已发送至 ${inviteEmail}` });
      }
      setInviteEmail('');
      fetchUsers();
    } catch (e: unknown) {
      setInviteResult({ type: 'error', message: getErrorMessage(e) });
    } finally {
      setInviting(false);
    }
  }

  // 筛选 + 搜索
  const filteredUsers = users.filter((u) => {
    if (filterMode === 'admin' && !u.is_faq_admin) return false;
    if (filterMode === 'normal' && u.is_faq_admin) return false;
    if (audienceMode === 'internal' && isExternalUser(u)) return false;
    if (audienceMode === 'external' && !isExternalUser(u)) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.name && u.name.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const adminCount = users.filter((u) => u.is_faq_admin).length;
  const externalCount = users.filter(isExternalUser).length;

  function getInitial(user: FaqUser) {
    if (user.name) return user.name[0].toUpperCase();
    if (user.email) return user.email[0].toUpperCase();
    return 'U';
  }

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  const ROLE_LABELS: Record<string, { label: string; color: string }> = {
    super_admin: { label: '超级管理员', color: 'bg-red-50 text-red-600 border-red-200' },
    admin: { label: '管理员', color: 'bg-amber-50 text-amber-600 border-amber-200' },
    advanced: { label: '高级用户', color: 'bg-purple-50 text-purple-600 border-purple-200' },
    intermediate: { label: '中级用户', color: 'bg-sky-50 text-sky-600 border-sky-200' },
    uploader: { label: '上传者', color: 'bg-teal-50 text-teal-600 border-teal-200' },
    basic: { label: '基础用户', color: 'bg-slate-50 text-slate-500 border-slate-200' },
  };

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      {/* 标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users size={22} className="text-sky-500" />
            用户管理
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            管理 FAQ 管理员权限 · 共 {users.length} 位用户，{adminCount} 位管理员，{externalCount} 位外部用户
          </p>
        </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => { setInviteOpen(true); setInviteResult(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white text-sm font-semibold rounded-xl hover:bg-indigo-600 transition-all shadow-sm"
        >
          <UserPlus size={16} />
          邀请用户
        </button>
        <button
          onClick={fetchUsers}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
          title="刷新"
        >
          <RefreshCw size={16} />
        </button>
      </div>
      </div>

      {/* 搜索 + 筛选 */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索邮箱或姓名..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
          />
        </div>
        <div className="flex items-center gap-1">
          {([
            { key: 'all', label: '全部' },
            { key: 'admin', label: 'FAQ 管理员' },
            { key: 'normal', label: '普通用户' },
          ] as const).map((item) => (
            <button
              key={item.key}
              onClick={() => setFilterMode(item.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                filterMode === item.key
                  ? 'bg-sky-500 text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {([
            { key: 'all', label: '全部来源' },
            { key: 'internal', label: '内部' },
            { key: 'external', label: '外部' },
          ] as const).map((item) => (
            <button
              key={item.key}
              onClick={() => setAudienceMode(item.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                audienceMode === item.key
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-400 hover:text-red-600 underline"
          >
            关闭
          </button>
        </div>
      )}

      {/* 加载 */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-sky-500" size={28} />
        </div>
      )}

      {/* 用户列表 */}
      {!loading && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {/* 表头（桌面端） */}
          <div className="hidden md:grid grid-cols-[1fr_110px_130px_140px_100px] gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-400 uppercase tracking-wider">
            <span>用户</span>
            <span>类型</span>
            <span>系统角色</span>
            <span>注册时间</span>
            <span className="text-center">FAQ 管理员</span>
          </div>

          {/* 列表 */}
          {filteredUsers.length === 0 ? (
            <div className="text-center py-16">
              <Users size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-400">
                {searchQuery ? '没有找到匹配的用户' : '暂无用户数据'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredUsers.map((user) => {
                const roleInfo = ROLE_LABELS[user.role] || {
                  label: user.role,
                  color: 'bg-slate-50 text-slate-500 border-slate-200',
                };
                const isToggling = togglingId === user.id;
                const isExternal = isExternalUser(user);

                return (
                  <div key={user.id}>
                    {/* 桌面端行 */}
                    <div className="hidden md:grid grid-cols-[1fr_110px_130px_140px_100px] gap-4 px-5 py-3.5 items-center hover:bg-slate-50/50 transition-colors">
                      {/* 用户信息 */}
                      <div className="flex items-center gap-3 min-w-0">
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt=""
                            className="w-9 h-9 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 text-white text-sm font-bold flex items-center justify-center shrink-0">
                            {getInitial(user)}
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">
                            {user.name || '未设置姓名'}
                          </p>
                          <p className="text-[11px] text-slate-400 truncate flex items-center gap-1">
                            <Mail size={10} />
                            {user.email}
                          </p>
                        </div>
                      </div>

                      {/* 用户类型 */}
                      <div>
                        <span
                          className={`inline-flex items-center px-2 py-1 text-[11px] font-medium rounded-lg border ${
                            isExternal
                              ? 'bg-amber-50 text-amber-600 border-amber-200'
                              : 'bg-sky-50 text-sky-600 border-sky-200'
                          }`}
                        >
                          {isExternal ? '外部' : '内部'}
                        </span>
                      </div>

                      {/* 系统角色 */}
                      <div>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-lg border ${roleInfo.color}`}
                        >
                          {roleInfo.label}
                        </span>
                      </div>

                      {/* 注册时间 */}
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <Calendar size={12} />
                        {formatDate(user.created_at)}
                      </div>

                      {/* FAQ 管理员切换 */}
                      <div className="flex justify-center">
                        {user.role === 'super_admin' && user.is_faq_admin ? (
                          <span
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-not-allowed opacity-80"
                            title="超级管理员不可取消授权"
                          >
                            <ShieldCheck size={13} />
                            已锁定
                          </span>
                        ) : (
                          <button
                            onClick={() => toggleFaqAdmin(user)}
                            disabled={isToggling}
                            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              user.is_faq_admin
                                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                                : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100 hover:text-slate-600'
                            } ${isToggling ? 'opacity-60' : ''}`}
                          >
                            {isToggling ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : user.is_faq_admin ? (
                              <ShieldCheck size={13} />
                            ) : (
                              <Shield size={13} />
                            )}
                            {user.is_faq_admin ? '已授权' : '未授权'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* 移动端卡片 */}
                    <div className="md:hidden px-4 py-3.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt=""
                              className="w-9 h-9 rounded-full object-cover shrink-0"
                            />
                          ) : (
                            <span className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 text-white text-sm font-bold flex items-center justify-center shrink-0">
                              {getInitial(user)}
                            </span>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {user.name || '未设置姓名'}
                            </p>
                            <p className="text-[11px] text-slate-400 truncate">
                              {user.email}
                            </p>
                          </div>
                        </div>
                        {/* FAQ 管理员切换 */}
                        <div className="shrink-0 ml-2">
                          {user.role === 'super_admin' && user.is_faq_admin ? (
                            <span
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200"
                            >
                              <ShieldCheck size={12} />
                              已锁定
                            </span>
                          ) : (
                            <button
                              onClick={() => toggleFaqAdmin(user)}
                              disabled={isToggling}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                                user.is_faq_admin
                                  ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                                  : 'bg-slate-50 text-slate-400 border border-slate-200'
                              } ${isToggling ? 'opacity-60' : ''}`}
                            >
                              {isToggling ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : user.is_faq_admin ? (
                                <ShieldCheck size={12} />
                              ) : (
                                <Shield size={12} />
                              )}
                              {user.is_faq_admin ? '已授权' : '未授权'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 ml-12">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-md border ${
                            isExternal
                              ? 'bg-amber-50 text-amber-600 border-amber-200'
                              : 'bg-sky-50 text-sky-600 border-sky-200'
                          }`}
                        >
                          {isExternal ? '外部' : '内部'}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-md border ${roleInfo.color}`}
                        >
                          {roleInfo.label}
                        </span>
                        <span className="text-[10px] text-slate-400 flex items-center gap-1">
                          <Calendar size={10} />
                          {formatDate(user.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 邀请用户弹窗 */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setInviteOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <UserPlus size={18} className="text-indigo-500" />
                邀请用户
              </h3>
              <button
                onClick={() => setInviteOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
              >
                <X size={16} />
              </button>
            </div>

            {/* 弹窗内容 */}
            <div className="p-6">
              <p className="text-sm text-slate-500 mb-4">
                输入邮箱地址邀请用户加入 FAQ 管理团队。系统将发送邀请邮件，用户设置密码后即可使用。
              </p>

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && inviteUser()}
                    placeholder="user@example.com"
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                    disabled={inviting}
                    autoFocus
                  />
                </div>
                <button
                  onClick={inviteUser}
                  disabled={inviting || !inviteEmail.trim()}
                  className="px-5 py-2.5 bg-indigo-500 text-white text-sm font-semibold rounded-xl hover:bg-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
                >
                  {inviting ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Send size={15} />
                  )}
                  发送
                </button>
              </div>

              {/* 结果反馈 */}
              {inviteResult && (
                <div className={`mt-4 flex items-start gap-2 p-3 rounded-xl text-sm ${
                  inviteResult.type === 'success'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-red-50 text-red-600 border border-red-200'
                }`}>
                  {inviteResult.type === 'success' ? (
                    <Check size={16} className="shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  )}
                  {inviteResult.message}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400">
                  💡 如果用户已有账号，将直接授予 FAQ 管理员权限，不会重复发送邮件。
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
